import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { formatAvailabilityDate, getFiscalYearQuarterString, formatFiscalYear, getAllQuarters } from '../../../lib/fiscal-year';
import { cn } from '../../../lib/utils';
import { formatDateUK } from '../../../lib/date-utils';
import type { Product, AllQuartersData, ProjectEvaluation } from '../../../api/shared/types';
import { Spinner } from '../../ui/spinner';

export interface ProductsCardProps {
  quarterInfo: ReturnType<typeof getAllQuarters>[0];
  product: Product;
  quartersData: AllQuartersData;
  onCardClick: (projectName: string, fiscalYearQuarter: string) => void;
  onProductSelect: (product: Product) => void;
  projectEvaluation?: ProjectEvaluation | null;
  epicCountsLoading?: boolean;
  statusLoading?: boolean;
}

const statusConfig = {
  'not-started': {
    label: 'Pending',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  },
  'in-progress': {
    label: 'Pending',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  },
  submitted: {
    label: 'Pending',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  },
  approved: {
    label: 'Approved',
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
  },
  rejected: {
    label: 'Pending',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  },
  reopened: {
    label: 'Re-opened',
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  },
};

export function ProductsCard({
  quarterInfo,
  product,
  quartersData,
  onCardClick,
  onProductSelect,
  projectEvaluation,
  epicCountsLoading,
  statusLoading
}: ProductsCardProps) {
  const fiscalYearQuarter = `${formatFiscalYear(quarterInfo.fiscalYear)} ${quarterInfo.quarter}`;
  const fiscalYearQuarterKey = getFiscalYearQuarterString(quarterInfo.fiscalYear, quarterInfo.quarter);
  const { isAvailable, endDate } = quarterInfo;

  const quarterData = quartersData[fiscalYearQuarterKey];

  const data: {
    epicCount: number;
    status: 'not-started' | 'in-progress' | 'submitted' | 'approved' | 'rejected' | 'reopened' | null;
    capexPercentage?: number;
    rndPercentage?: number;
    submittedBy?: string;
    submittedByName?: string;
    submittedAt?: string;
    approvedBy?: string;
    approvedByName?: string;
    approvedAt?: string;
  } = quarterData ? {
    epicCount: quarterData.epicCount,
    status: (
      quarterData.status === 'reopened' || quarterData.status === 'withdrawn'
        ? 'reopened'
        : quarterData.status === 'pendingapproval'
        ? 'submitted'
        : quarterData.status === 'approved' || quarterData.status === 'archived'
        ? 'approved'
        : quarterData.status === 'rejected'
        ? 'rejected'
        : (quarterData.status === 'draft' || quarterData.status === 'inprogress') && quarterData.hasAssessedEpics
        ? 'in-progress'
        : 'not-started'
    ) as 'not-started' | 'in-progress' | 'submitted' | 'approved' | 'rejected' | 'reopened',
    capexPercentage: quarterData.capexPercentage,
    rndPercentage: quarterData.rndPercentage,
    submittedBy: quarterData.submittedBy,
    submittedByName: quarterData.submittedByName,
    submittedAt: quarterData.submittedAt,
    approvedBy: quarterData.approvedBy,
    approvedByName: quarterData.approvedByName,
    approvedAt: quarterData.approvedAt,
  } : {
    epicCount: 0,
    status: 'not-started',
  };

  const hasQuarterData = quarterData != null;
  const hasEpics = data.epicCount > 0;
  const isClickable = isAvailable && hasQuarterData && product.project_name != null && hasEpics;

  const handleClick = () => {
    if (isClickable) {
      onProductSelect(product);
      onCardClick(product.project_name, fiscalYearQuarter);
    }
  };

  if (!isAvailable) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="relative opacity-40 cursor-not-allowed">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">{product.project_name}</CardTitle>
                <p className="text-sm text-muted-foreground">{fiscalYearQuarter}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-muted-foreground"></div>
                <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                  <p className="text-sm font-medium text-center px-4">
                    Available after {formatAvailabilityDate(endDate)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent>
            <p>This quarter will become available after {formatAvailabilityDate(endDate)}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (isAvailable && !hasQuarterData) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="relative opacity-40 cursor-not-allowed">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">{product.project_name}</CardTitle>
                <p className="text-sm text-muted-foreground">{fiscalYearQuarter}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-muted-foreground"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-sm font-medium text-center px-4">
                    No project data available for this quarter
                  </p>
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent>
            <p>No project data available for this quarter</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (isAvailable && !hasEpics) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="relative opacity-60 cursor-not-allowed">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-muted-foreground">{product.project_name}</CardTitle>
                <p className="text-sm text-muted-foreground">{fiscalYearQuarter}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-2xl font-bold text-muted-foreground">0</p>
                  <p className="text-sm text-muted-foreground">Closed Items</p>
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent>
            <p>No closed items in this quarter</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const epicCount = data?.epicCount ?? 0;
  const status = data?.status ?? 'not-started';
  const safeStatus = status === null ? 'not-started' : status;
  const statusInfo = statusConfig[safeStatus] ?? statusConfig['not-started'];

  return (
    <Card
      className={cn(
        'transition-all duration-200',
        isClickable ? 'cursor-pointer hover:shadow-lg hover:-translate-y-1' : 'cursor-default'
      )}
      onClick={isClickable ? handleClick : undefined}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">{product.project_name}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{fiscalYearQuarter}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {statusLoading && <Spinner className="size-4" />}
            <Badge className={statusInfo.className}>{statusInfo.label}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold">{epicCountsLoading ? '—' : epicCount}</p>
            {epicCountsLoading && <Spinner className="size-5" />}
          </div>
          <p className="text-sm text-muted-foreground">Closed Items</p>
        </div>

        {/* != null guards against both null and undefined (API can return null, not undefined) */}
        {status === 'approved' && data?.capexPercentage != null && data?.rndPercentage != null && (
          <div className="pt-3 border-t space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">CapEx</span>
              <span className="text-sm font-semibold">{data.capexPercentage.toFixed(0)}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">R&D</span>
              <span className="text-sm font-semibold">{data.rndPercentage.toFixed(0)}%</span>
            </div>
          </div>
        )}

        {projectEvaluation && (
          <div className="pt-3 border-t space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Project Evaluation</p>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">CapEx</span>
              <span className="text-sm font-semibold">{(projectEvaluation.capex_percentage ?? 0).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">R&D</span>
              <span className="text-sm font-semibold">{(projectEvaluation.rnd_percentage ?? 0).toFixed(0)}%</span>
            </div>
          </div>
        )}

        {(status === 'submitted' || status === 'approved' || status === 'rejected') && (data?.submittedByName || data?.approvedByName) && (
          <div className="pt-3 border-t space-y-2">
            {data?.submittedByName && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Submitted by</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm font-semibold cursor-default">{data.submittedByName}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{data.submittedBy}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
            {data?.submittedAt && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Submitted on</span>
                <span className="text-sm font-semibold">
                  {formatDateUK(data.submittedAt)}
                </span>
              </div>
            )}
            {data?.approvedByName && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {status === 'approved' ? 'Approved by' : 'Rejected by'}
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm font-semibold cursor-default">{data.approvedByName}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{data.approvedBy}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
            {data?.approvedAt && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {status === 'approved' ? 'Approved on' : 'Rejected on'}
                </span>
                <span className="text-sm font-semibold">
                  {formatDateUK(data.approvedAt)}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
